import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import { MobileLayout, SimpleLayout } from 'src/layouts';

import {
  AppGuide,
  AppGuideDetail,
  Calendar,
  Craving,
  CravingDiary,
  CravingForecastDetail,
  DrJin,
  DrugQuestion,
  DrugQuestionForm,
  EditMemberInfo,
  EditProfile,
  EditQuitSmokingDiary,
  ExistAccount,
  Faq,
  FindId,
  FindIdEnd,
  Ftnd,
  FtndEnd,
  FtndForm,
  HomeV2,
  LanguageSettings,
  Login,
  MedicalQuestion,
  MedicalQuestionEnd,
  MedicalQuestionForm,
  MedicineSettings,
  MissionDetail,
  MissionHistory,
  MissionHistoryDetail,
  MyPage,
  Notification,
  NotificationSettings,
  OrganicGlowDemo,
  Page403,
  Page404,
  Page500,
  PageError,
  PersonaSettings,
  QuitSmokingAim,
  QuitSmokingDateSettings,
  QuitSmokingDiary,
  Record,
  RecordEnd,
  Register,
  RegisterEnd,
  RegisterForm,
  ResetPwd,
  ResetPwdEnd,
  ResetPwdForm,
  SmokingPattern,
  Supporter,
  Terms,
  TermsSettings,
  Verification,
  Version,
  Withdrawal,
} from './elements';
import { PATH_AUTH, PATH_PAGE } from './paths';

// ----------------------------------------------------------------------

const routes: RouteObject[] = [
  {
    children: [
      {
        errorElement: <PageError />,
        element: <MobileLayout />,
        children: [
          {
            path: PATH_PAGE.page403,
            element: <Page403 />,
          },
          {
            path: PATH_PAGE.page404,
            element: <Page404 />,
          },
          {
            path: PATH_PAGE.page500,
            element: <Page500 />,
          },
          { path: '*', element: <Navigate to={PATH_PAGE.page404} replace /> },
        ],
      },
    ],
  },
  {
    element: <SimpleLayout />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.login,
            element: (
              <GuestGuard>
                <Login />
              </GuestGuard>
            ),
          },
          {
            path: PATH_PAGE.organicGlowDemo,
            element: <OrganicGlowDemo />,
          },
        ],
      },
    ],
  },
  {
    element: <MobileLayout needFooter={false} />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.findId.root,
            element: (
              <GuestGuard>
                <FindId />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.findId.end,
            element: (
              <GuestGuard>
                <FindIdEnd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.root,
            element: (
              <GuestGuard>
                <ResetPwd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.form,
            element: (
              <GuestGuard>
                <ResetPwdForm />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.end,
            element: (
              <GuestGuard>
                <ResetPwdEnd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.register.root,
            element: (
              <GuestGuard>
                <Register />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.register.existAccount,
            element: (
              <GuestGuard>
                <ExistAccount />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.register.form,
            element: (
              <GuestGuard>
                <RegisterForm />
              </GuestGuard>
            ),
          },
          {
            path: PATH_PAGE.quitSmokingDateSettings,
            element: (
              <AuthGuard>
                <QuitSmokingDateSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.ftnd.root,
            element: (
              <AuthGuard>
                <Ftnd />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.ftnd.form,
            element: (
              <AuthGuard>
                <FtndForm />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.ftnd.end,
            element: (
              <AuthGuard>
                <FtndEnd />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.drugQuestion.root,
            element: (
              <AuthGuard>
                <DrugQuestion />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.drugQuestion.form,
            element: (
              <AuthGuard>
                <DrugQuestionForm />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.root,
            element: (
              <AuthGuard>
                <MedicalQuestion />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.form,
            element: (
              <AuthGuard>
                <MedicalQuestionForm />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.end,
            element: (
              <AuthGuard>
                <MedicalQuestionEnd />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicineSettings.root,
            element: (
              <AuthGuard>
                <MedicineSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.edit.profile,
            element: (
              <AuthGuard>
                <EditProfile />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.edit.memberInfo,
            element: (
              <AuthGuard>
                <EditMemberInfo />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.terms,
            element: (
              <AuthGuard>
                <TermsSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.termsDetail,
            element: (
              <AuthGuard>
                <Terms />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.notification,
            element: (
              <AuthGuard>
                <NotificationSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.faq,
            element: (
              <AuthGuard>
                <Faq />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.version,
            element: (
              <AuthGuard>
                <Version />
              </AuthGuard>
            ),
          },
          // 앱 사용 가이드는 로그인 화면에서도 열리므로 로그인 검사(AuthGuard)를 두지 않는다 (개인정보 처리 방침과 같음)
          {
            path: PATH_PAGE.myPage.customerService.appGuide,
            element: <AppGuide />,
          },
          {
            path: PATH_PAGE.myPage.customerService.appGuideDetail,
            element: <AppGuideDetail />,
          },
          {
            path: PATH_PAGE.policy,
            element: <Terms />,
          },
          {
            path: PATH_PAGE.drJin.root,
            element: (
              <AuthGuard>
                <DrJin />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.craving.root,
            element: (
              <AuthGuard>
                <Craving />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.craving.record.root,
            element: (
              <AuthGuard>
                <Record />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.craving.record.end,
            element: (
              <AuthGuard>
                <RecordEnd />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.calendar.editQuitSmokingDiary,
            element: (
              <AuthGuard>
                <EditQuitSmokingDiary />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.calendar.quitSmokingAim,
            element: (
              <AuthGuard>
                <QuitSmokingAim />
              </AuthGuard>
            ),
          },

          {
            path: PATH_PAGE.supporter,
            element: (
              <AuthGuard>
                <Supporter />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.language,
            element: (
              <AuthGuard>
                <LanguageSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.persona,
            element: (
              <AuthGuard>
                <PersonaSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.withdrawal,
            element: (
              <AuthGuard>
                <Withdrawal />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.notification,
            element: (
              <AuthGuard>
                <Notification />
              </AuthGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <MobileLayout needHeader={false} needFooter={false} />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.register.verification.root,
            element: (
              <GuestGuard>
                <Verification />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.register.end,
            element: (
              <GuestGuard>
                <RegisterEnd />
              </GuestGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        errorElement: <PageError />,
        element: <MobileLayout />,
        children: [
          {
            path: PATH_PAGE.home,
            element: (
              <AuthGuard>
                <HomeV2 />
              </AuthGuard>
            ),
          },

          {
            path: PATH_PAGE.calendar.root,
            element: (
              <AuthGuard>
                <Calendar />
              </AuthGuard>
            ),
          },

          {
            path: PATH_PAGE.myPage.root,
            element: (
              <AuthGuard>
                <MyPage />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.smokingPattern.root,
            element: (
              <AuthGuard>
                <SmokingPattern />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.cravingForecast.root,
            element: (
              <AuthGuard>
                <CravingForecastDetail />
              </AuthGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        errorElement: <PageError />,
        element: <MobileLayout needFooter={false} />,
        children: [
          {
            path: PATH_PAGE.mission.history,
            element: (
              <AuthGuard>
                <MissionHistory />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.mission.historyDetail,
            element: (
              <AuthGuard>
                <MissionHistoryDetail />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.mission.detail,
            element: (
              <AuthGuard>
                <MissionDetail />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.calendar.quitSmokingDiary,
            element: (
              <AuthGuard>
                <QuitSmokingDiary />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.calendar.cravingDiary,
            element: (
              <AuthGuard>
                <CravingDiary />
              </AuthGuard>
            ),
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes, {
  basename: '/',
  future: {
    v7_fetcherPersist: true,
    v7_relativeSplatPath: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true, // v7의 동작 미리 적용
  },
});

export default router;
